/* eslint-disable no-shadow */
import { PageTypes } from "~/templates/transactions/types";
import { VASP } from "~/types/vaspid";

export interface TransactionListParameters {
  vaspDID: string;
  sort?: string | null;
  transactionAsset?: string | null;
  transactionType?: TransactionType | null;
  txGroup?: string | null;
  txDirection?: string | null;
  page?: number | null;
  resultsPerPage?: number | null;
  q?: string | null;
  decrypt?: boolean | null;
  includeActions?: boolean | null;
  status?: string | null;
  jurisdictions?: (string | string[]) | null;
  vasps?: (string | string[]) | null;
  start?: string | null;
  end?: string | null;
  wallet?: string | null;
}

export interface TransactionBulkRequest {
  action: "approve" | "accept" | "cancel" | "confirm" | "decline" | "reject" | "notReady" | "redirect";
  originatorVASPdid: string | null;
  beneficiaryVASPdid: string | null;
  originAddress?: string | null;
  destinationAddress?: string | null;
  reason?: string | null;
  beneficiaryVASPDidTo?: string | null;
}

export interface TransactionCancelRequest {
  id: string;
  vasp_did?: string; // Needed when calling from beneficiary
  reason?: string | null;
}

export interface TransactionRejectRequest {
  id: string;
  reason?: string | null;
}

export interface TransactionRedirectRequest {
  id: string;
  to: string;
}

export interface TransactionBulkRedirectRequest {
  transaction_ids: string[];
  to: string;
}

export interface TransactionDeclineRequest {
  id: string;
  reason?: string | null;
}

export interface TransactionUpdateRequest {
  id: string;
  beneficiaryDid?: string | null;
  beneficiaryVASPdid?: string | null;
  beneficiaryVASPname?: string | null;
  beneficiary?: any | null;
  originatorDid?: string | null;
  originatorVASPdid?: string | null;
  originatorVASPname?: string | null;
  originator?: any | null;
  notificationEmail?: string | null;
  txHash?: string | null;
  destination?: string | null;
  origin?: string | null;
  isNonCustodial?: boolean | null;
  comment?: string | null;
}

export interface TransactionValidateRequest {
  transactionAsset: string;
  destination: string;
  transactionAmount: string;
  originatorVASPdid: string;
  originatorEqualsBeneficiary: boolean;
  beneficiaryVASPdid?: string | null;
  beneficiaryVASPname?: string | null;
  beneficiaryName?: string | null;
  beneficiaryAccountNumber?: string | null;
  beneficiaryAddress?: AddressInput | null;
  beneficiaryProof?: OwnershipProofInput | null;
}

export interface TransactionCreateRequest {
  transactionAsset: string;
  transactionAmount: string;
  originatorDid?: string | null;
  beneficiaryDid?: string | null;
  originatorVASPdid?: string | null;
  beneficiaryVASPdid?: string | null;
  beneficiaryVASPname?: string | null;
  beneficiaryVASPwebsite?: string | null;
  transactionBlockchainInfo?: TransactionBlockchainInfoInput | null;
  originator?: OriginatorInput | null;
  beneficiary?: BeneficiaryInput | null;
  encrypted?: string | null;
  protocol?: TravelRuleProtocol | null;
  notificationEmail?: string | null;
  originatorProof?: OwnershipProofInput | null;
  beneficiaryProof?: OwnershipProofInput | null;
  skipBeneficiaryDataValidation: boolean;
  onBehalfOfMyself?: boolean | null;
  customAssetPrice?: {
    priceUSD: number;
    decimals: number;
  };
}

export type SortOperator = "ASC" | "DESC";

export type MultipleValues = {
  operator: "IN" | "NOT_IN";
  values: string[];
};

export type SingleValue = {
  operator: "EQ" | "NEQ" | "LT" | "LTE" | "GT" | "GTE" | "MATCHES" | "NOT_MATCHES";
  value: string | number;
};

export type PropertyExistence = {
  operator: "EMPTY" | "NOT_EMPTY";
};

export type RangeValue<T extends string | number = string> = {
  operator: "BETWEEN" | "NOT_BETWEEN";
  from: T;
  to: T;
};

type FilterValue = MultipleValues | SingleValue | PropertyExistence | RangeValue;

type Filter = {
  property_name: string;
} & FilterValue;

export type FilterOperator = FilterValue["operator"];

type Sort = {
  property_name: string;
  direction: SortOperator;
};

export type ExportRequestMetadata = {
  export_type: PageTypes;
  filters?: Filter[];
  sorts?: Sort[];
};

export type TransactionExportRequestCreateRequest = {
  vasp_did: string;
  created_by: string;
  metadata: ExportRequestMetadata;
};

export type ExportRequest = {
  id: string;
  vasp_did: string;
  status: TransactionExportStatus;
  created_by: string;
  created_at: string;
  metadata: ExportRequestMetadata;
};

export type TransactionExportRequestCreateResponse = {
  export_request: ExportRequest;
};

export type TransactionExportRequestsResponse = {
  pagination: Pagination;
  export_requests: ExportRequest[];
};

export type TransactionExportStatus = "REQUESTED" | "PROCESSING" | "PROCESSED" | "EXPIRED" | "FAILED";

export type Pagination = {
  page: number;
  per_page: number;
  total: number;
};

export interface Actions {
  action: string;
  createdAt: string;
  transactionId: string;
}
export interface Transaction {
  id: string;
  status: TransactionStatus;
  transactionType: TransactionType;
  transactionAsset: string;
  transactionAmount: string;
  transactionAssetDecimals: number;
  amountInLocalCurrency: AmountInLocalCurrency;
  beneficiaryAmountInLocalCurrency: AmountInLocalCurrency;
  originatorVASPdid: string | null;
  originatorVASP: VASP | null;
  beneficiaryVASPdid: string | null;
  beneficiaryVASP: VASP | null;
  originatorDid: string | null;
  beneficiaryDid: string;
  protocol: TravelRuleProtocol | null;
  protocolData?: TravelRuleProtocolData | null;
  transactionBlockchainInfo: TransactionBlockchainInfoInput | null;
  ivms101Encrypted: string | null;
  ivms101: IVMS101 | null;
  updatedAt: any | null;
  createdAt: any;
  originatorCustomer: Customer | null;
  beneficiaryCustomer: Customer | null;
  originatorRef: string | null;
  beneficiaryRef: string | null;
  originatorProof: OwnershipProofInput | null;
  beneficiaryProof: OwnershipProofInput | null;
  actions?: Actions[];
  custom_asset_price?: {
    priceUSD: number;
    decimals: number;
  };
  blockchainTransactionTimestamp: any | null;
  businessDaysSinceRequestForInfo?: number;
  comments?: TransactionComment[] | null;
}

export enum TransactionStatus {
  ACCEPTED = "ACCEPTED",
  ACK = "ACK",
  CANCELLED = "CANCELLED",
  INCOMPLETE = "INCOMPLETE",
  MERGED = "MERGED",
  NEW = "NEW",
  REJECTED = "REJECTED",
  DECLINED = "DECLINED",
  SAVED = "SAVED",
  SENT = "SENT",
  WAITING_FOR_INFORMATION = "WAITING_FOR_INFORMATION",
  MISSING_BENEFICIARY_DATA = "MISSING_BENEFICIARY_DATA",
  WAITING_FOR_TR = "WAITING_FOR_TR",
  NOT_READY = "NOT_READY",
}

export enum TransactionType {
  BELOW_THRESHOLD = "BELOW_THRESHOLD",
  NON_CUSTODIAL = "NON_CUSTODIAL",
  TRAVELRULE = "TRAVELRULE",
  UNKNOWN = "UNKNOWN",
}

export enum TravelRuleProtocol {
  OpenVASP = "OpenVASP",
  TRLight = "TRLight",
  TRP = "TRP",
}

export interface TravelRuleProtocolData {
  notificationEmails: string[];
  trLightData: { accessLinks: { id: string; expiration: string }[] };
}

export interface AmountInLocalCurrency {
  amountInLocalCurrency: number;
  currency: string;
}

export interface NationalIdentification {
  nationalIdentifier?: string | null;
  nationalIdentifierType?: NationalIdentifierTypeCode | null;
  countryOfIssue?: string | null;
  registrationAuthority?: string | null;
}

export enum NationalIdentifierTypeCode {
  ARNU = "ARNU",
  CCPT = "CCPT",
  DRLC = "DRLC",
  FIIN = "FIIN",
  IDCD = "IDCD",
  LEIX = "LEIX",
  MISC = "MISC",
  RAID = "RAID",
  SOCS = "SOCS",
  TXID = "TXID",
}

export interface TransactionBlockchainInfoInput {
  txHash?: string | null;
  origin?: string | null;
  destination?: string | null;
}

export interface AddressInput {
  addressType?: AddressTypeCode | null;
  department?: string | null;
  subDepartment?: string | null;
  streetName?: string | null;
  buildingNumber?: string | null;
  buildingName?: string | null;
  floor?: string | null;
  postBox?: string | null;
  room?: string | null;
  postCode?: string | null;
  townName?: string | null;
  townLocationName?: string | null;
  districtName?: string | null;
  countrySubDivision?: string | null;
  addressLine?: string[] | null;
  country?: string | null;
}

export enum AddressTypeCode {
  BIZZ = "BIZZ",
  GEOG = "GEOG",
  HOME = "HOME",
}

export enum SignedMessageTypes {
  CheckboxConfirmation = "checkbox",
  SelfDeclarationConfirmation = "self-declaration",
  PersonalSignEIP191 = "eip-191",
  PersonalSignEIP712 = "eip-712",
  PersonalSignBIP137 = "bip-137",
  PersonalSignXPUB = "xpub",
}

export type SignedMessageProof = {
  type?: SignedMessageTypes;
  proof?: string | null;
  attestation?: string;
  address?: string;
  wallet_provider?: string;
};

export type ScreenshotProof = {
  type: "screenshot";
  s3Key: string;
};

export type OwnershipProofInput = SignedMessageProof | ScreenshotProof;

export interface OriginatorInput {
  originatorPersons?: PersonInput[] | null;
  accountNumber?: string[] | null;
}

export interface BeneficiaryInput {
  beneficiaryPersons?: PersonInput[] | null;
  accountNumber?: string[] | null;
}

export interface PersonInput {
  naturalPerson?: NaturalPersonInput | null;
  legalPerson?: LegalPersonInput | null;
}

export interface NaturalPersonInput {
  name?: NaturalPersonNameInput[] | null;
  geographicAddress?: (AddressInput | null)[] | null;
  nationalIdentification?: NationalIdentification | null;
  customerIdentification?: string | null;
  dateAndPlaceOfBirth?: DateAndPlaceOfBirthInput | null;
  countryOfResidence?: string | null;
}

export interface NaturalPersonNameInput {
  nameIdentifier?: NaturalPersonNameIDInput[] | null;
  localNameIdentifier?: LocalNaturalPersonNameIDInput[] | null;
  phoneticNameIdentifier?: LocalNaturalPersonNameIDInput[] | null;
}

export interface NaturalPersonNameIDInput {
  primaryIdentifier?: string | null;
  secondaryIdentifier?: string | null;
  nameIdentifierType?: NaturalPersonNameTypeCode | null;
}

export interface LocalNaturalPersonNameIDInput {
  primaryIdentifier?: string | null;
  secondaryIdentifier?: string | null;
  nameIdentifierType?: NaturalPersonNameTypeCode | null;
}

export enum NaturalPersonNameTypeCode {
  ALIA = "ALIA",
  BIRT = "BIRT",
  LEGL = "LEGL",
  MAID = "MAID",
  MISC = "MISC",
}

export interface LegalPersonInput {
  name?: LegalPersonNameInput | null;
  geographicAddress?: (AddressInput | null)[] | null;
  customerNumber?: string | null;
  nationalIdentification?: NationalIdentification | null;
  countryOfRegistration?: string | null;
}

export interface LegalPersonNameInput {
  nameIdentifier?: LegalPersonNameIDInput[] | null;
  localNameIdentifier?: LegalPersonNameIDInput[] | null;
  phoneticNameIdentifier?: LegalPersonNameIDInput[] | null;
}

export interface LegalPersonNameIDInput {
  legalPersonName?: string | null;
  legalPersonNameIdentifierType?: string | null;
}

export interface LegalPersonNameIDInput {
  legalPersonName?: string | null;
  legalPersonNameIdentifierType?: string | null;
}

export interface IVMS101 {
  originator: IVMS101Originator | null;
  beneficiary: IVMS101Beneficiary | null;
}

export interface IVMS101Originator {
  originatorPersons: IVMS101Persons[] | null;
  accountNumber: string[] | null;
}

export interface IVMS101Beneficiary {
  beneficiaryPersons: IVMS101Persons[] | null;
  accountNumber: string[] | null;
}

export interface IVMS101Persons {
  naturalPerson: IVMS101NaturalPerson | null;
  legalPerson: IVMS101LegalPerson | null;
}

export interface IVMS101NaturalPerson {
  name: IVMS101NaturalPersonName[] | null;
  geographicAddress: (IVMS101PersonGeographicAddress | null)[] | null;
  nationalIdentification: NationalIdentification | null;
  customerIdentification: string | null;
  dateAndPlaceOfBirth: DateAndPlaceOfBirthInput | null;
  countryOfResidence: string | null;
}

export interface IVMS101NaturalPersonName {
  nameIdentifier: IVMS101NaturalPersonNameIdentifier[] | null;
  localNameIdentifier: IVMS101NaturalPersonNameIdentifier[] | null;
  phoneticNameIdentifier: IVMS101NaturalPersonNameIdentifier[] | null;
}

export interface IVMS101NaturalPersonNameIdentifier {
  primaryIdentifier: string | null;
  secondaryIdentifier: string | null;
  nameIdentifierType: NaturalPersonNameTypeCode | null;
}

export interface IVMS101LegalPerson {
  name: IVMS101LegalPersonName | null;
  geographicAddress: (IVMS101PersonGeographicAddress | null)[] | null;
  customerNumber: string | null;
  nationalIdentification: NationalIdentification | null;
  dateOfRegistration: string | null;
  countryOfRegistration: string | null;
}

export interface IVMS101LegalPersonName {
  nameIdentifier: IVMS101LegalPersonNameIdentifier[] | null;
  localNameIdentifier: IVMS101LegalPersonNameIdentifier[] | null;
  phoneticNameIdentifier: IVMS101LegalPersonNameIdentifier[] | null;
}

export interface IVMS101LegalPersonNameIdentifier {
  legalPersonName: string | null;
  legalPersonNameIdentifierType: string | null;
}

export interface IVMS101PersonGeographicAddress {
  addressType: AddressTypeCode | null;
  department: string | null;
  subDepartment: string | null;
  streetName: string | null;
  buildingNumber: string | null;
  buildingName: string | null;
  floor: string | null;
  postBox: string | null;
  room: string | null;
  postCode: string | null;
  townName: string | null;
  townLocationName: string | null;
  districtName: string | null;
  countrySubDivision: string | null;
  addressLine: string[] | null;
  country: string | null;
}

export interface Customer {
  id: string;
  did: string;
  vaspDID: string;
  customerRef: string | null;
}

export interface DateAndPlaceOfBirthInput {
  dateOfBirth?: string | null;
  placeOfBirth?: string | null;
}

export interface TransactionComment {
  vaspDID: string;
  createdAt: Date;
  createdBy: { userDID: string; userName: string };
  text: string;
}
